import { useTranslation } from '@thoughtspot/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { useDeleteSmtpMutation, useGetSmtpQuery, useSetSmtpMutation, } from "../../../services/system/system-service";
import { GenericError } from '../../generic-error/generic-error.container';
import { Content, CreateModal, Header, WithQueryHandler, } from '../common';
import { ActionButtons, HOST_REGEX, IP_REGEX, LearnMore, Mode, TranslationsKeys as TranslationsKeysCommon, } from '../common/constants';
import { getConfig } from '../common/form-builder-config';
import { getFormFields, parseFormData } from '../common/utils';
import { TranslationsKeys } from './constants';
import styles from './smtp.module.scss';
const pageName = 'smtp';
const intialData = {
    configured_status: false,
};
const SmtpContainer = ({ onSuccess, onError, showLoader, hideLoader, }) => {
    const { t } = useTranslation();
    const [data, setData] = useState({});
    const [mode, setMode] = useState(Mode.VIEW);
    const [formFields, setFormFields] = useState([]);
    const [smtpPostData, setSmtpPostData] = useState(null);
    const [hostNotReachablePopup, setHostNotReachablePopup] = React.useState(false);
    const getTranslations = (key, args) => t(`${key}`, args);
    const formConfig = useMemo(() => getConfig(pageName), []);
    const onSaveSuccess = (response) => {
        setMode(Mode.VIEW);
        if (response?.Error) {
            onError(response?.Error);
        }
        else {
            const newData = response.Data || {};
            setData(newData?.configured_status ? newData : intialData);
            if (!newData.is_host_reachable) {
                hideLoader();
                setHostNotReachablePopup(true);
            }
            else {
                onSuccess(getTranslations(TranslationsKeys.TOAST_SUCCESS));
            }
        }
    };
    const { data: smtpData, loading: isLoading, error } = useGetSmtpQuery();
    const fetchSmtp = () => {
        if (smtpData && !isLoading && smtpData.Data) {
            hideLoader();
            const newData = smtpData.Data || {};
            setData(newData?.configured_status ? newData : intialData);
        }
        else if (smtpData && !isLoading && smtpData.Error) {
            onError(getTranslations(TranslationsKeys.MESSAGE_API_FAILURE));
        }
    };
    const [updateSmtp] = useSetSmtpMutation(response => onSaveSuccess(response), onError, smtpPostData);
    const [deleteSmtp] = useDeleteSmtpMutation(response => {
        setMode(Mode.VIEW);
        const newData = response.Data || {};
        setData(newData?.configured_status ? newData : intialData);
        onSuccess(getTranslations(TranslationsKeys.DELETE_SUCCESS));
    }, onError);
    const getFieldConfig = (field) => {
        switch (field.key) {
            case 'smtp_auth_status':
                return {
                    children: [
                        {
                            label: getTranslations(TranslationsKeysCommon.YES),
                            value: 'true',
                        },
                        {
                            label: getTranslations(TranslationsKeysCommon.NO),
                            value: 'false',
                        },
                    ],
                    label: field.config.label,
                    orientation: 'tray',
                };
            case 'configured_status':
                return {
                    ...field.config,
                    isTextValueColorLight: !data.configured_status,
                };
            default:
                return field.config;
        }
    };
    const updateFormFields = (formData = {}, errorMessages = {}) => {
        const fieldsToShow = [];
        if (!data.configured_status && mode === 'view') {
            const configuredStatus = formConfig.find((config) => config.key === 'configured_status');
            fieldsToShow.push({
                ...configuredStatus,
                configCallback: getFieldConfig,
            });
        }
        else {
            formConfig.forEach((config) => {
                if (mode === Mode.VIEW && config.view) {
                    fieldsToShow.push({
                        ...config,
                        configCallback: getFieldConfig,
                    });
                }
                else if (mode === Mode.EDIT && config.elementType) {
                    if (['username', 'password'].includes(config.key)) {
                        if (formData.smtp_auth_status) {
                            fieldsToShow.push({
                                ...config,
                                configCallback: getFieldConfig,
                            });
                        }
                    }
                    else {
                        fieldsToShow.push({
                            ...config,
                            configCallback: getFieldConfig,
                        });
                    }
                }
            });
        }
        const newFormFields = getFormFields(formData, fieldsToShow, errorMessages, mode);
        setFormFields(newFormFields);
    };
    useEffect(() => {
        showLoader();
    }, []);
    useEffect(() => {
        fetchSmtp();
    }, [smtpData]);
    useEffect(() => {
        updateFormFields(data);
    }, [data, mode]);
    useEffect(() => {
        if (smtpPostData) {
            showLoader();
            updateSmtp();
        }
    }, [smtpPostData]);
    const validateForm = (currentFormData = {}, editedField = '') => {
        let anyInValid = false;
        if (editedField) {
            const editedData = currentFormData.filter((i) => i.id === editedField)[0] ||
                {};
            if (editedData?.valid === false) {
                anyInValid = true;
            }
        }
        else {
            currentFormData.forEach((i) => {
                if (i && i.valid === false) {
                    anyInValid = true;
                }
            });
        }
        let isValid = false;
        if (!anyInValid) {
            if (!editedField || ['from_name'].includes(editedField)) {
                const fromNameVal = currentFormData.filter((i) => i.id === 'from_name')[0]
                    .value || '';
                let validFromName = false;
                if (HOST_REGEX.test(fromNameVal)) {
                    validFromName = true;
                }
                else {
                    const fromNameArray = fromNameVal.split('.');
                    if (fromNameArray.length === 4) {
                        validFromName = fromNameArray.every((i) => IP_REGEX.test(i));
                    }
                }
                if (!validFromName) {
                    const errorMessages = {};
                    errorMessages.from_name = getTranslations(TranslationsKeysCommon.VALID_FIELDNAME, { field_name: t(TranslationsKeysCommon.FROM_DOMAIN) });
                    currentFormData.forEach((i) => {
                        if (i.valid === false) {
                            errorMessages[i.id] = i?.validation?.error;
                        }
                    });
                    isValid = false;
                    const formData = parseFormData(currentFormData);
                    updateFormFields(formData, errorMessages);
                }
                else {
                    isValid = true;
                }
            }
        }
        return isValid;
    };
    const onSave = (currentFormData) => {
        const isFormValid = validateForm(currentFormData);
        if (isFormValid) {
            const postData = {
                ...parseFormData(currentFormData),
            };
            setSmtpPostData({ ...postData });
        }
    };
    const actionHandler = (type, currentFormData = {}) => {
        switch (type) {
            case ActionButtons.EDIT:
                setMode(Mode.EDIT);
                break;
            case ActionButtons.BACK:
            case ActionButtons.CANCEL:
                setMode(Mode.VIEW);
                break;
            case ActionButtons.SAVE:
                onSave(currentFormData);
                break;
            case ActionButtons.DELETE:
                showLoader();
                deleteSmtp();
                break;
            default:
                setMode(Mode.EDIT);
                break;
        }
    };
    const handleIsEdited = (currentFormData, id) => {
        if (id === 'smtp_auth_status') {
            const formData = parseFormData(currentFormData);
            updateFormFields(formData);
        }
        else {
            validateForm(currentFormData, id);
        }
    };
    const renderContent = () => {
        const config = {
            title: getTranslations(TranslationsKeys.FORM_TITLE),
            formFields,
        };
        const actions = {
            primary: {
                type: ActionButtons.SAVE,
                text: getTranslations(TranslationsKeys.SAVE_BUTTON),
                returnData: true,
            },
            secondary: {
                type: ActionButtons.CANCEL,
                text: getTranslations(TranslationsKeysCommon.CANCEL_BUTTON),
            },
        };
        if (data.configured_status) {
            actions.link = {
                type: ActionButtons.DELETE,
                text: getTranslations(TranslationsKeys.DELETE_BUTTON),
                askConfirm: true,
            };
        }
        return (React.createElement("div", { className: styles.smtpContentWrapper },
            React.createElement(Content, { key: `${pageName}_${mode}`, config: config, mode: mode, backConfirm: true, actions: actions, pageName: pageName, showHeader: false, actionHandler: actionHandler, onEdit: handleIsEdited })));
    };
    const onConfirm = () => setHostNotReachablePopup(false);
    const headerActions = [
        {
            type: ActionButtons.EDIT,
            title: data.configured_status
                ? getTranslations(TranslationsKeys.EDIT_BUTTON)
                : getTranslations(TranslationsKeys.CONFIGURE_BUTTON),
        },
    ];
    return error ? (React.createElement(GenericError, null)) : (React.createElement(React.Fragment, null,
        hostNotReachablePopup && (React.createElement(CreateModal, { pageName: pageName, action: "hostNotReachable", onConfirm: onConfirm })),
        React.createElement("div", { className: styles.smtpContainer },
            !isLoading && (React.createElement(React.Fragment, null,
                React.createElement(Header, { title: getTranslations(TranslationsKeys.TITLE), subTitle: getTranslations(TranslationsKeys.SUBTITLE), titleSize: "large", learnMoreKey: LearnMore.SMTP, mode: mode, actions: mode === Mode.VIEW ? headerActions : undefined, actionHandler: actionHandler }))),
            !isLoading && renderContent())));
};
export const Smtp = () => (React.createElement(WithQueryHandler, null,
    React.createElement(SmtpContainer, null)));
//# sourceMappingURL=smtp.container.js.map